import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import API, { formatApiError } from "../api/client";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { LogIn } from "lucide-react";

export default function LoginPage() {
  const { user, login, embedded } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [testUsers, setTestUsers] = useState([]);
  const isEmbedded = embedded || (typeof window !== "undefined" && window.parent !== window);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    API.get("/auth/test-users").then(r => setTestUsers(r.data)).catch(() => {});
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleTestLogin = async (tu) => {
    setError("");
    setLoading(true);
    try {
      await login(tu.email, tu.password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9FB] flex items-center justify-center p-4" data-testid="login-page">
      {isEmbedded ? (
        <div className="text-center" data-testid="hub-connecting">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#111216] border-t-transparent mx-auto mb-4" />
          <p className="text-sm text-[#686B74]">Connecting to CargoDash Hub...</p>
          <p className="text-xs text-[#686B74] mt-2">If this takes too long, please refresh the Hub page.</p>
        </div>
      ) : (
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-10 h-10 rounded-lg bg-[#111216] flex items-center justify-center mx-auto mb-4">
            <LogIn className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight font-['Manrope'] text-[#111216]">
            Sign in
          </h1>
          <p className="text-sm text-[#686B74] mt-1">Enter your credentials to continue</p>
        </div>

        {/* Form */}
        <div className="bg-white border border-[#EBEBEB] rounded-lg p-6 shadow-sm">
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-[#686B74] mb-1.5 block">Email</label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="email@company.com"
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#686B74] mb-1.5 block">Password</label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter password"
                required
              />
            </div>
            {error && (
              <p className="text-sm text-[#E24A4A]" data-testid="login-error">{error}</p>
            )}
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#111216] hover:bg-[#2B2D31] text-white"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </div>

        {/* Test Users */}
        {testUsers.length > 0 && (
          <div className="mt-6">
            <div className="text-center mb-3">
              <span className="text-xs text-[#686B74] uppercase tracking-wider">Quick Login</span>
            </div>
            <div className="space-y-2">
              {testUsers.map((tu, i) => (
                <button
                  key={tu.email}
                  data-testid={`login-test-user-${i + 1}`}
                  onClick={() => handleTestLogin(tu)}
                  disabled={loading}
                  className="w-full flex items-center gap-3 p-3 bg-white border border-[#EBEBEB] rounded-lg hover:bg-[#F4F5F8] hover:border-[#4353FF]/30 transition-all duration-150 group"
                >
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={tu.avatar_url} alt={tu.name} />
                    <AvatarFallback className="text-xs bg-[#111216] text-white">
                      {tu.name.split(" ").map(n => n[0]).join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left flex-1">
                    <p className="text-sm font-medium text-[#111216] group-hover:text-[#4353FF] transition-colors">
                      {tu.name}
                    </p>
                    <p className="text-xs text-[#686B74]">{tu.email}</p>
                  </div>
                  <span className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded bg-[#F4F5F8] text-[#686B74]">
                    {tu.role}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
